import { useEffect, useRef } from 'react';

import { Button } from 'design-system';
import { LayoutDashboard } from 'lucide-react';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import {
  DrawerStep,
  useDrawerSteps
} from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { useDashboardEditLockContext } from '@/features/dashboard/state/DashboardEditLockContext';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

import { DashboardActionButtons } from '../../components/DashboardActionButtons';
import { DashboardLayout } from '../../components/DashboardLayout';
import type { DashboardStep } from '../../constants/DashboardStep';
import { AddAppButton } from './components/AddAppButton';

export const EmptyDashboard = ({
  tabNames = [],
  activeTab = 0,
  onTabChange,
  setStep,
  title,
  isNewDashboard = false
}: {
  tabNames?: string[];
  activeTab?: number;
  onTabChange?: (index: number) => void;
  setStep: (step: DashboardStep) => void;
  title: string;
  /** Whether this is the new-dashboard creation flow rather than an existing
   * dashboard with a blank tab. Hides the Edit button, since there is nothing
   * yet to edit. */
  isNewDashboard?: boolean;
}) => {
  const { setDrawerStep } = useDrawerSteps();
  const { requestEditLock } = useDashboardEditLockContext();

  const onClick = () => {
    requestEditLock(() => {
      setDrawerStep(DrawerStep.SelectApp);
    });
  };

  const { setMode, mode } = useAppForm();
  const initialModeRef = useRef(mode);

  useEffect(() => {
    setMode('create');
    return () => {
      setMode(initialModeRef.current);
    };
  }, [setMode]);

  return (
    <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
      <DashboardLayout
        activeTab={activeTab}
        renderRight={
          <DashboardActionButtons
            hideEditButton={isNewDashboard}
            setStep={setStep}
          />
        }
        tabNames={tabNames}
        title={title}
        onTabChange={onTabChange}
      >
        <section className='mt-4 mb-2 flex flex-col items-center justify-center gap-4'>
          <h2 className='font-heading text-center text-2xl font-semibold'>
            Start Building Your Dashboard
          </h2>
          <p className='mb-2 text-center text-lg text-muted-foreground'>
            Organize your perfect workspace by adding apps
          </p>
          <Button
            size='lg'
            onClick={onClick}
          >
            <LayoutDashboard
              aria-hidden='true'
              className='size-5'
            />
            Add Your First App
          </Button>
        </section>
        <div className='grid w-full grid-cols-1 gap-2 md:grid-cols-2 md:gap-6'>
          <AddAppButton onClick={onClick} />
          <AddAppButton onClick={onClick} />
        </div>
      </DashboardLayout>
    </PageInformationContext>
  );
};
